/**
 * Customers Page Component
 * 
 * This component displays the customers management interface
 * with customer list and detailed customer information.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { useTranslations } from '../context/TranslationsContext';
import { customersAPI } from '../api';
import CustomersTable from '../components/customers/CustomersTable';
import CustomerDetailsModal from '../components/customers/CustomerDetailsModal';
import SearchBar from '../components/common/SearchBar';
import Pagination from '../components/common/Pagination';

const CustomersPage = () => {
  const { state, actions } = useApp();
  const { translations } = useTranslations();
  const t = translations[state.currentLanguage];

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchCustomers = async () => {
      if (!state.token) return;
      
      actions.setLoading(true);
      try {
        const customers = await customersAPI.getCustomers(state.token);
        actions.setCustomers(customers);
      } catch (error) {
        console.error('Error fetching customers:', error);
      } finally {
        actions.setLoading(false);
      }
    };

    fetchCustomers();
  }, [state.token]);

  // Filter customers based on search term
  const filteredCustomers = useMemo(() => {
    if (!searchTerm) return state.customers;
    
    return state.customers.filter(customer => 
      customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone?.includes(searchTerm) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [state.customers, searchTerm]);

  // Paginate filtered customers
  const paginatedCustomers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredCustomers.slice(startIndex, endIndex);
  }, [filteredCustomers, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);

  const handleSearchChange = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (state.loading) {
    return <div className="loading">جاري التحميل...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h2 className="section-title">{t.customers}</h2>
        <div className="page-actions">
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            placeholder="البحث في العملاء..."
            className="w-64"
          />
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <CustomersTable 
          customers={paginatedCustomers}
          onViewDetails={(customer) => actions.setSelectedCustomer(customer)}
        />
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalItems={filteredCustomers.length}
          itemsPerPage={itemsPerPage}
        />
      </div>

      {state.selectedCustomer && (
        <CustomerDetailsModal
          customer={state.selectedCustomer}
          onClose={() => actions.setSelectedCustomer(null)}
        />
      )}
    </div>
  );
};

export default CustomersPage;
